import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Chip, Select, MenuItem, FormControl, InputLabel,
  Tooltip
} from '@mui/material';
import { policyService } from '../services/policyService';

const ENGINE_LABELS = { ajv: 'JSON Schema', opa: 'OPA/Rego', hybrid: 'Hybrid' };

function PolicyDashboard() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterFramework, setFilterFramework] = useState('');
  const [opaHealth, setOpaHealth] = useState(null);
  const [form, setForm] = useState({
    policy_name: '', policy_version: '1.0', regulatory_framework: 'CSRD',
    effective_from: '', rejection_action: 'flag',
    validation_rules: '{}', scope_filter: '{}',
    policy_engine: 'ajv', opa_policy_path: '',
  });

  useEffect(() => {
    loadPolicies();
    checkOPA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFramework]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await policyService.listPolicies({
        regulatory_framework: filterFramework || undefined,
      });
      setPolicies(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkOPA = async () => {
    try {
      const health = await policyService.checkOPAHealth();
      setOpaHealth(health?.status ?? 'unavailable');
    } catch {
      setOpaHealth('unavailable');
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...form,
        validation_rules: form.policy_engine === 'opa' ? {} : JSON.parse(form.validation_rules),
        scope_filter: JSON.parse(form.scope_filter),
        opa_policy_path: form.policy_engine !== 'ajv' ? form.opa_policy_path : undefined,
      };
      await policyService.createPolicy(payload);
      setDialogOpen(false);
      setForm({
        policy_name: '', policy_version: '1.0', regulatory_framework: 'CSRD',
        effective_from: '', rejection_action: 'flag',
        validation_rules: '{}', scope_filter: '{}',
        policy_engine: 'ajv', opa_policy_path: '',
      });
      loadPolicies();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  const handleActivate = async (id) => {
    try {
      await policyService.activatePolicy(id);
      loadPolicies();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  const handleRetire = async (id) => {
    try {
      await policyService.retirePolicy(id);
      loadPolicies();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  const statusColor = (status) => {
    const map = { active: 'success', draft: 'warning', retired: 'default', deprecated: 'default' };
    return map[status] || 'default';
  };

  const engineColor = (engine) => {
    const map = { ajv: 'default', opa: 'primary', hybrid: 'secondary' };
    return map[engine] || 'default';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4">Policy-as-Code Dashboard</Typography>
        <Chip
          label={`OPA: ${opaHealth || 'checking...'}`}
          size="small"
          color={opaHealth === 'healthy' ? 'success' : 'default'}
          variant="outlined"
        />
      </Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage regulatory compliance policies (CSRD, ISSB, ISO 14064-1, GHG Protocol) with AJV or OPA/Rego engines
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>Create Policy</Button>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Framework</InputLabel>
          <Select value={filterFramework} label="Framework" onChange={(e) => setFilterFramework(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="CSRD">CSRD</MenuItem>
            <MenuItem value="ISSB">ISSB</MenuItem>
            <MenuItem value="ISO14064-1">ISO 14064-1</MenuItem>
            <MenuItem value="GHGP">GHG Protocol</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Policy Name</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Framework</TableCell>
                <TableCell>Engine</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Effective From</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map((p) => (
                <TableRow key={p.compliance_policy_id || p.policy_id}>
                  <TableCell>
                    {p.opa_policy_path ? (
                      <Tooltip title={`OPA: ${p.opa_policy_path}`} arrow>
                        <span>{p.policy_name}</span>
                      </Tooltip>
                    ) : p.policy_name}
                  </TableCell>
                  <TableCell>{p.policy_version}</TableCell>
                  <TableCell><Chip label={p.regulatory_framework} size="small" /></TableCell>
                  <TableCell>
                    <Chip
                      label={ENGINE_LABELS[p.policy_engine] || p.policy_engine || 'AJV'}
                      size="small"
                      color={engineColor(p.policy_engine)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell><Chip label={p.status} size="small" color={statusColor(p.status)} /></TableCell>
                  <TableCell>{p.effective_from ? new Date(p.effective_from).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    {p.status === 'draft' && <Button size="small" onClick={() => handleActivate(p.compliance_policy_id || p.policy_id)}>Activate</Button>}
                    {p.status === 'active' && <Button size="small" color="warning" onClick={() => handleRetire(p.compliance_policy_id || p.policy_id)}>Retire</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {policies.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No policies found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Compliance Policy</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={8}>
              <TextField fullWidth label="Policy Name" value={form.policy_name} onChange={(e) => setForm({ ...form, policy_name: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Version" value={form.policy_version} onChange={(e) => setForm({ ...form, policy_version: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Framework</InputLabel>
                <Select value={form.regulatory_framework} label="Framework" onChange={(e) => setForm({ ...form, regulatory_framework: e.target.value })}>
                  <MenuItem value="CSRD">CSRD</MenuItem>
                  <MenuItem value="ISSB">ISSB</MenuItem>
                  <MenuItem value="ISO14064-1">ISO 14064-1</MenuItem>
                  <MenuItem value="GHGP">GHG Protocol</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Policy Engine</InputLabel>
                <Select value={form.policy_engine} label="Policy Engine" onChange={(e) => setForm({ ...form, policy_engine: e.target.value })}>
                  <MenuItem value="ajv">JSON Schema (AJV)</MenuItem>
                  <MenuItem value="opa">OPA/Rego</MenuItem>
                  <MenuItem value="hybrid">Hybrid (Both)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth type="date" label="Effective From" InputLabelProps={{ shrink: true }} value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
            </Grid>
            {form.policy_engine !== 'ajv' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="OPA Policy Path"
                  placeholder="carbon_measure/ghg_protocol/scope_classification"
                  helperText="Rego package path registered in OPA (e.g., carbon_measure/csrd_esrs/scope3_verification)"
                  value={form.opa_policy_path}
                  onChange={(e) => setForm({ ...form, opa_policy_path: e.target.value })}
                />
              </Grid>
            )}
            {form.policy_engine !== 'opa' && (
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4} label="Validation Rules (JSON Schema)" value={form.validation_rules} onChange={(e) => setForm({ ...form, validation_rules: e.target.value })} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Scope Filter (JSON)" value={form.scope_filter} onChange={(e) => setForm({ ...form, scope_filter: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PolicyDashboard;
